<template>
  <Dialog
    v-model:visible="visible"
    modal
    :style="{ width: '30vw' }"
    :header="t('sites.singleSite.financialConfig.mealPlans.form.domains.dialog.header')"
    :draggable="false"
    :closable="false"
  >
    <Grid>
      <Col12>
        <Form
          id="meal-plan-domains-dialog-form"
          :form-id="FinancialFormIDs.MEAL_PLAN_DOMAINS_FORM"
          :schema="domainsSchema"
          :initialValues="initialValues"
          @onValidateSuccess="submitForm"
          ref="formComponent"
        >
          <template v-slot="{ isFormValid, form }">
            <Grid>
              <Col7>
                <TextField
                  id="meal-plan-domain-input"
                  name="domain"
                  :label="
                    t('sites.singleSite.financialConfig.mealPlans.form.domains.dialog.domain')
                  "
                  :placeholder="
                    t(
                      'sites.singleSite.financialConfig.mealPlans.form.domains.dialog.domainPlaceholder',
                    )
                  "
                />
              </Col7>

              <Col5>
                <Button
                  class="mt-8"
                  outlined
                  :label="
                    t('sites.singleSite.financialConfig.mealPlans.form.domains.dialog.addButton')
                  "
                  :disabled="!isFormValid || isEmpty(form?.values?.domain)"
                  @click="save"
                />
              </Col5>
            </Grid>
          </template>
        </Form>
      </Col12>

      <Col12>
        <span class="text-sm font-medium mr-2">{{
          t('sites.singleSite.financialConfig.mealPlans.form.domains.dialog.dividerLabel')
        }}</span>
        <Divider class="mt-1 mb-0" />
      </Col12>

      <Col12 v-if="!isEmpty(domains)">
        <Grid class="overflow-y-auto max-h-[14rem]">
          <template v-for="(item, index) in domains" :key="index">
            <Col12>
              <div class="flex items-center justify-between">
                <div class="flex">
                  <span>{{ item }}</span>
                </div>

                <div class="flex">
                  <IconButton
                    :id="`remove-domain-${index}`"
                    class="c-icon-button"
                    @click="removeDomain(index)"
                  >
                    <template #icon>
                      <span class="material-symbols-rounded text-[1.5rem]">delete</span>
                    </template>
                  </IconButton>
                </div>
              </div>
            </Col12>
          </template>
        </Grid>
      </Col12>
    </Grid>

    <template #footer>
      <Button
        :label="t('sites.singleSite.financialConfig.mealPlans.form.domains.dialog.cancel')"
        text
        @click="cancelDialog"
      />
      <Button
        :label="t('sites.singleSite.financialConfig.mealPlans.form.domains.dialog.save')"
        @click="confirmDialog"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, ref, type PropType } from 'vue';
import type { Form } from '@centric-os/components/src/composed/forms/form';
import { cloneDeep, isEmpty } from 'lodash-es';
import { useMealPlanSchema } from '../../../../../../composables';
import { FinancialFormIDs } from '../../../enums';

// Define emits
const emits = defineEmits(['addDomains']);

// Define visible vmodel
const visible = defineModel('visible', {
  type: Boolean,
  required: true,
});

const props = defineProps({
  domains: {
    type: Array as PropType<string[]>,
    required: true,
  },
});

// Set localization
const { t } = useI18n();

// Set domain schema
const { domainsSchema } = useMealPlanSchema();

// Ref for domains list
const domains = ref<string[]>(props.domains);

// Define refs for form component
const formComponent = ref<InstanceType<typeof Form>>();

// Computed for initial values
const initialValues = computed((): { domain: string | null } => ({
  domain: null,
}));

// Submit form
const save = async (): Promise<void> => {
  formComponent.value?.validate();
};

// Method to submit form
const submitForm = (values: any): void => {
  domains.value = cloneDeep([...domains.value, values.domain]);
  formComponent.value?.form?.resetForm();
};

// Method to confirm dialog
const confirmDialog = (): void => {
  emits('addDomains', domains.value);
  cancelDialog();
};

// Method to cancel dialog
const cancelDialog = (): void => {
  visible.value = false;
  domains.value = [];
  formComponent.value?.form?.resetForm();
};

// Method to remove domain
const removeDomain = (index: number): void => {
  domains.value.splice(index, 1);
};
</script>
